import React, { useState, useEffect, useRef } from "react";
import { postData } from "../../services/api";
import toastr from "toastr";
import { classNames } from "../../helper/classNames";

// ─── Terminal line ─────────────────────────────────────────
const TerminalLine = ({ text, type = "info" }) => {
  const colors = {
    info: "text-emerald-400",
    success: "text-cyan-300",
    warn: "text-yellow-300",
    dim: "text-slate-500",
    white: "text-slate-200",
  };
  return (
    <div
      className={classNames(
        "flex items-start gap-2 font-mono text-sm",
        colors[type] || colors.info,
      )}
    >
      <span className="text-slate-600 select-none flex-shrink-0">›</span>
      <span>{text}</span>
    </div>
  );
};

// ─── Braille spinner ───────────────────────────────────────
const Spinner = () => {
  const [frame, setFrame] = useState(0);
  const frames = ["⠋", "⠙", "⠹", "⠸", "⠼", "⠴", "⠦", "⠧", "⠇", "⠏"];
  useEffect(() => {
    const i = setInterval(() => setFrame((f) => (f + 1) % frames.length), 80);
    return () => clearInterval(i);
  }, []);
  return (
    <span className="font-mono text-sm text-emerald-400">{frames[frame]}</span>
  );
};

// ─── Step dot ──────────────────────────────────────────────
const StepDot = ({ active, done, label, sublabel }) => (
  <div className="flex flex-col items-center gap-1">
    <div
      className={classNames(
        "w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all duration-300",
        done
          ? "bg-emerald-500 border-emerald-500 text-white"
          : active
            ? "bg-transparent border-emerald-400 text-emerald-400"
            : "bg-transparent border-slate-700 text-slate-600",
      )}
    >
      {done ? <i className="fa-solid fa-check text-[10px]" /> : label}
    </div>
    <span
      className={classNames(
        "text-[10px] font-medium tracking-wider uppercase",
        active
          ? "text-emerald-400"
          : done
            ? "text-slate-400"
            : "text-slate-600",
      )}
    >
      {sublabel}
    </span>
  </div>
);

// ─── Main component ────────────────────────────────────────
const PositionWideTierPopup = ({
  isOpen,
  setIsOpen = () => {},
  loadList = () => {},
}) => {
  const [step, setStep] = useState(1);
  const [terminalLines, setTerminalLines] = useState([]);
  const [processing, setProcessing] = useState(false); // fetch phase
  const [fetchDone, setFetchDone] = useState(false); // fetch complete → show picker
  const [existingTiers, setExistingTiers] = useState([]);
  const [selectedTierId, setSelectedTierId] = useState("");
  const [newTierName, setNewTierName] = useState("");
  const [tierMode, setTierMode] = useState("select"); // "select" | "create"
  const [assigning, setAssigning] = useState(false); // submit phase
  const terminalRef = useRef(null);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setTerminalLines([]);
      setProcessing(false);
      setFetchDone(false);
      setExistingTiers([]);
      setSelectedTierId("");
      setNewTierName("");
      setTierMode("select");
      setAssigning(false);
    }
  }, [isOpen]);

  // Auto-scroll terminal
  useEffect(() => {
    if (terminalRef.current)
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
  }, [terminalLines]);

  const closeModal = () => setIsOpen({ open: false });

  const addLine = (text, type = "info", delay = 0) =>
    new Promise((res) =>
      setTimeout(() => {
        setTerminalLines((prev) => [
          ...prev,
          { text, type, id: Date.now() + Math.random() },
        ]);
        res();
      }, delay),
    );

  // ── Step 1 → 2: fetch existing tiers ──────────────────
  const runFetch = async () => {
    setStep(2);
    setProcessing(true);

    try {
      await addLine("Initialising migration process...", "dim", 300);
      await addLine("Connecting to database...", "info", 700);

      const profileResult = await postData("profile/details", {});
      await addLine(
        `Profile fetched: ${profileResult?.data?.company_name ?? "OK"}`,
        "success",
        400,
      );
      await addLine("Verifying current tier configuration...", "info", 300);

      const tierType = profileResult?.data?.tier_type;
      if (tierType === "position-wide") {
        await addLine("Position-Wide Tier is already enabled.", "warn", 300);
        await addLine("No changes required.", "dim", 200);
        setProcessing(false);
        setFetchDone(true);
        return;
      }

      await addLine("Company-Wide Tier detected ✓", "success", 500);
      await addLine("Backing up current tier assignments...", "dim", 600);

      // Fetch existing tiers to show in picker
      const tierResult = await postData("filter-item/list", {
        type: "position-group",
      });
      const tiers = tierResult?.data ?? [];

      if (tiers.length > 0) {
        await addLine(`Found ${tiers.length} existing tier(s):`, "info", 400);
        for (const t of tiers) {
          await addLine(
            `  · ${t.name}${t.is_default ? " (current default)" : ""}`,
            "dim",
            150,
          );
        }
      } else {
        await addLine(
          "No existing tiers found — create one below.",
          "warn",
          400,
        );
      }

      await addLine("──────────────────────────────────────────", "dim", 200);
      await addLine("Select a default tier or create a new one.", "white", 300);

      setExistingTiers(tiers);
      const defaultTier = tiers.find((t) => t.is_default);
      if (defaultTier) setSelectedTierId(defaultTier._id);
      else if (tiers.length === 0) setTierMode("create");

      setProcessing(false);
      setFetchDone(true);
    } catch (err) {
      await addLine(`Error: ${err.message}`, "warn", 200);
      await addLine("Process halted. Please try again.", "dim", 200);
      setProcessing(false);
    }
  };

  // ── Step 2 submit ──────────────────────────────────────
  // Sequence:
  //   CREATE mode → POST company/create-default-tier  (creates tier + assigns to all positions/headings)
  //   SELECT mode → POST company/assing-tier          (assigns existing tier to all positions/headings)
  //   ALWAYS last → POST company/set-tier-type        (updates company.tier_type = "position-wide")
  const submitDefaultTier = async () => {
    const isCreate = tierMode === "create";

    if (isCreate && !newTierName.trim()) {
      toastr.error("Please enter a name for the new tier.");
      return;
    }
    if (!isCreate && !selectedTierId) {
      toastr.error("Please select a tier.");
      return;
    }

    setAssigning(true);

    try {
      if (isCreate) {
        // ── A: Create new tier (repo also assigns it to all positions internally) ──
        await addLine(
          `Creating new tier "${newTierName.trim()}"...`,
          "info",
          0,
        );

        const createResult = await postData("company/create-default-tier", {
          name: newTierName.trim(),
        });

        if (!createResult || createResult.status !== 200) {
          throw new Error(createResult?.message || "Failed to create tier.");
        }

        await addLine(`Tier "${newTierName.trim()}" created ✓`, "success", 300);
        await addLine(
          "All positions and headings assigned to new tier ✓",
          "success",
          400,
        );
      } else {
        // ── B: Assign existing tier to all positions/headings ──
        await addLine("Assigning selected tier to all positions...", "info", 0);

        const assignResult = await postData("company/assing-tier", {
          id: selectedTierId,
        });

        if (!assignResult || assignResult.status !== 200) {
          throw new Error(assignResult?.message || "Failed to assign tier.");
        }

        await addLine("All positions and headings updated ✓", "success", 400);
      }

      // ── C: Update company tier_type → position-wide (always last) ──
      await addLine(
        "Updating company tier type → position-wide...",
        "info",
        300,
      );

      const setTypeResult = await postData("company/set-tier-type", {
        tier_type: "position-wide",
      });

      if (!setTypeResult || setTypeResult.status !== 200) {
        throw new Error(
          setTypeResult?.message || "Failed to update tier type.",
        );
      }

      await addLine("Company tier type updated ✓", "success", 300);
      await addLine("──────────────────────────────────────────", "dim", 200);
      await addLine("Migration complete. No data was lost.", "white", 300);

      setAssigning(false);
      setTimeout(() => setStep(3), 900);
    } catch (err) {
      await addLine(`Error: ${err.message}`, "warn", 200);
      await addLine("Process halted. Please try again.", "dim", 200);
      setAssigning(false);
    }
  };

  if (!isOpen) return null;

  const canClose = !processing && !assigning;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={canClose ? closeModal : undefined}
      />

      <div className="relative w-full max-w-xl bg-[#0d1117] border border-slate-700/60 rounded-2xl shadow-2xl overflow-hidden">
        {/* Title bar */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700/60 bg-[#161b22]">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500/80" />
            <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
            <div className="w-3 h-3 rounded-full bg-green-500/80" />
            <span className="ml-3 text-xs font-mono text-slate-500 tracking-wider">
              tier-migration.sh
            </span>
          </div>
          {canClose && (
            <button
              onClick={closeModal}
              className="text-slate-600 hover:text-slate-300 transition-colors"
            >
              <i className="fa-light fa-times text-sm" />
            </button>
          )}
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-4 px-6 py-4 border-b border-slate-700/30">
          <StepDot
            active={step === 1}
            done={step > 1}
            label="1"
            sublabel="Confirm"
          />
          <div
            className={classNames(
              "flex-1 h-px max-w-[80px] transition-all duration-500",
              step > 1 ? "bg-emerald-500/60" : "bg-slate-700",
            )}
          />
          <StepDot
            active={step === 2}
            done={step > 2}
            label="2"
            sublabel="Configure"
          />
          <div
            className={classNames(
              "flex-1 h-px max-w-[80px] transition-all duration-500",
              step > 2 ? "bg-emerald-500/60" : "bg-slate-700",
            )}
          />
          <StepDot active={step === 3} done={false} label="3" sublabel="Done" />
        </div>

        {/* Content */}
        <div className="px-6 py-5 min-h-[300px] flex flex-col">
          {/* ── Step 1 ── */}
          {step === 1 && (
            <div className="flex flex-col gap-5 flex-1">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mt-0.5">
                  <i className="fa-solid fa-triangle-exclamation text-amber-400 text-sm" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-base mb-1">
                    Enable Position-Wide Tier
                  </h3>
                  <p className="text-slate-500 text-xs font-mono tracking-wide uppercase">
                    Migration required
                  </p>
                </div>
              </div>

              <div className="bg-[#161b22] border border-slate-700/50 rounded-xl p-4 space-y-3 text-sm text-slate-300 leading-relaxed">
                <p>
                  Your company is currently using the{" "}
                  <span className="text-amber-300 font-medium">
                    Company-Wide Tier
                  </span>{" "}
                  system. Enabling{" "}
                  <span className="text-emerald-300 font-medium">
                    Position-Wide Tier
                  </span>{" "}
                  will give you the ability to manage custom tiers based on
                  specific positions.
                </p>
                <div className="border-t border-slate-700/50 pt-3 space-y-2">
                  {[
                    "All existing positions will be switched to a default tier",
                    "No data will be lost — current position assignments remain intact",
                    "You can then create custom tiers and assign rules per position",
                  ].map((text) => (
                    <div
                      key={text}
                      className="flex items-start gap-2 text-slate-400 text-xs"
                    >
                      <i className="fa-solid fa-check text-emerald-400 mt-0.5 flex-shrink-0" />
                      <span>{text}</span>
                    </div>
                  ))}
                </div>
                <p className="text-slate-500 text-xs border-t border-slate-700/50 pt-3">
                  Are you sure you want to proceed? This will give you more
                  flexibility without affecting existing data.
                </p>
              </div>

              <div className="flex gap-3 mt-auto">
                <button
                  onClick={closeModal}
                  className="flex-1 py-2.5 rounded-lg border border-slate-700 text-slate-400 text-sm font-medium hover:bg-slate-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={runFetch}
                  className="flex-1 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-bold transition-colors flex items-center justify-center gap-2"
                >
                  <i className="fa-solid fa-bolt text-xs" />
                  Proceed
                </button>
              </div>
            </div>
          )}

          {/* ── Step 2 ── */}
          {step === 2 && (
            <div className="flex flex-col flex-1 gap-3">
              {/* Status bar */}
              <div className="flex items-center gap-2">
                {(processing || assigning) && <Spinner />}
                <span className="text-xs font-mono text-slate-500 tracking-wider">
                  {processing
                    ? "fetching data..."
                    : assigning
                      ? "applying changes..."
                      : fetchDone
                        ? "ready"
                        : "error"}
                </span>
                {!processing && !assigning && fetchDone && (
                  <span className="text-xs font-mono text-emerald-400">✓</span>
                )}
              </div>

              {/* Terminal */}
              <div
                ref={terminalRef}
                className="bg-[#0a0e14] rounded-xl border border-slate-700/40 p-4 overflow-y-auto space-y-1.5 max-h-[180px] font-mono"
              >
                {terminalLines.map((line) => (
                  <TerminalLine
                    key={line.id}
                    text={line.text}
                    type={line.type}
                  />
                ))}
                {(processing || assigning) && (
                  <div className="flex items-center gap-2 mt-1">
                    <Spinner />
                    <span className="font-mono text-xs text-slate-600 animate-pulse">
                      processing...
                    </span>
                  </div>
                )}
              </div>

              {/* Tier picker — shown only when fetch done and not submitting */}
              {fetchDone && !assigning && (
                <div className="bg-[#161b22] border border-slate-700/50 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      Default Tier
                    </span>
                    {existingTiers.length > 0 && (
                      <button
                        onClick={() =>
                          setTierMode(
                            tierMode === "select" ? "create" : "select",
                          )
                        }
                        className="text-xs text-emerald-400 hover:text-emerald-300 transition-colors font-mono underline"
                      >
                        {tierMode === "select"
                          ? "+ create new tier"
                          : "← choose existing"}
                      </button>
                    )}
                  </div>

                  {/* Select existing */}
                  {tierMode === "select" && existingTiers.length > 0 && (
                    <div className="space-y-1.5 max-h-[140px] overflow-y-auto pr-1">
                      {existingTiers.map((tier) => (
                        <button
                          key={tier._id}
                          onClick={() => setSelectedTierId(tier._id)}
                          className={classNames(
                            "w-full flex items-center justify-between px-3 py-2.5 rounded-lg border text-sm transition-all",
                            selectedTierId === tier._id
                              ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-300"
                              : "border-slate-700/60 text-slate-400 hover:border-slate-600 hover:text-slate-300",
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className={classNames(
                                "w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 transition-all",
                                selectedTierId === tier._id
                                  ? "border-emerald-400 bg-emerald-400"
                                  : "border-slate-600",
                              )}
                            />
                            <span className="font-mono text-xs">
                              {tier.name}
                            </span>
                          </div>
                          {tier.is_default && (
                            <span className="text-[10px] bg-slate-700 text-slate-400 px-1.5 py-0.5 rounded font-mono">
                              current default
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Create new */}
                  {tierMode === "create" && (
                    <div>
                      <input
                        type="text"
                        value={newTierName}
                        onChange={(e) => setNewTierName(e.target.value)}
                        onKeyDown={(e) =>
                          e.key === "Enter" && submitDefaultTier()
                        }
                        placeholder="e.g. Standard Tier"
                        autoFocus
                        className="w-full bg-[#0d1117] border border-slate-700 rounded-lg px-3 py-2.5 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-emerald-500/60 focus:ring-1 focus:ring-emerald-500/20 transition-all font-mono"
                      />
                      <p className="text-slate-600 text-xs mt-1.5 font-mono">
                        this tier will be created and assigned to all positions
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Action buttons */}
              {fetchDone && !assigning && (
                <div className="flex gap-3 mt-auto">
                  <button
                    onClick={() => setStep(1)}
                    className="py-2.5 px-4 rounded-lg border border-slate-700 text-slate-400 text-sm font-medium hover:bg-slate-800 transition-colors"
                  >
                    <i className="fa-solid fa-arrow-left text-xs mr-1.5" />
                    Back
                  </button>
                  <button
                    onClick={submitDefaultTier}
                    disabled={
                      (tierMode === "select" && !selectedTierId) ||
                      (tierMode === "create" && !newTierName.trim())
                    }
                    className="flex-1 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:bg-emerald-900 disabled:text-emerald-700 text-black text-sm font-bold transition-colors flex items-center justify-center gap-2"
                  >
                    <i className="fa-solid fa-check text-xs" />
                    Apply &amp; Continue
                  </button>
                </div>
              )}

              {/* Error state — go back */}
              {!processing && !assigning && !fetchDone && (
                <button
                  onClick={() => setStep(1)}
                  className="self-start text-xs text-slate-500 hover:text-slate-300 underline transition-colors"
                >
                  ← Go back
                </button>
              )}
            </div>
          )}

          {/* ── Step 3: Done ── */}
          {step === 3 && (
            <div className="flex flex-col flex-1 items-center justify-center gap-5 py-4 text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-500/10 border-2 border-emerald-500/40 flex items-center justify-center">
                <i className="fa-solid fa-check text-emerald-400 text-2xl" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg mb-2">
                  Migration Complete
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed max-w-sm">
                  Position-Wide Tier is now enabled. All existing positions have
                  been assigned to the default tier. You can now create and
                  manage custom tiers per position.
                </p>
              </div>
              <div className="bg-[#161b22] border border-slate-700/50 rounded-xl p-3 w-full text-left space-y-1.5">
                {[
                  "Company tier type → position-wide ✓",
                  "All positions assigned to default tier ✓",
                  "No data was lost ✓",
                ].map((line) => (
                  <div
                    key={line}
                    className="flex items-center gap-2 text-xs font-mono text-emerald-400"
                  >
                    <span className="text-slate-600">›</span>
                    {line}
                  </div>
                ))}
              </div>
              <button
                onClick={() => {
                  loadList();
                  closeModal();
                }}
                className="w-full py-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-bold transition-colors flex items-center justify-center gap-2"
              >
                <i className="fa-solid fa-arrow-right text-xs" />
                Finish
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PositionWideTierPopup;
